import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { setupApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { SETUP } from "@/constants/testIds";
import {
  ArrowLeft, CheckCircle2, XCircle, AlertTriangle, ChevronRight,
  Play, Send, ThumbsUp, Zap, Shield, Clock, RefreshCw, Lock, Unlock
} from "lucide-react";

const LIFECYCLE = ["NOT_STARTED", "IN_PROGRESS", "CONFIGURED", "VALIDATED", "APPROVED", "ACTIVE"];

export default function SetupDomainDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const load = () => {
    setLoading(true);
    setupApi.getDomain(code)
      .then(r => setDomain(r.data))
      .catch(() => toast.error("Failed to load domain"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [code]);

  if (loading) return <LoadingState />;
  if (!domain) return <div className="p-6 text-[#706E6B]">Domain not found</div>;

  const canValidate = ["SETUP_ADMIN", "TENANT_ADMIN", "PLATFORM_ADMIN"].includes(user?.role);
  const canApprove = ["SETUP_APPROVER", "TENANT_ADMIN", "PLATFORM_ADMIN"].includes(user?.role);
  const canActivate = ["SETUP_ADMIN", "TENANT_ADMIN", "PLATFORM_ADMIN"].includes(user?.role);

  const status = domain.status;
  const blockers = domain.blockers || [];
  const valResults = domain.validation_results || [];
  const deps = domain.dependencies || [];
  const history = domain.status_history || [];
  const readiness = domain.readiness || {};

  const doAction = async (action, label) => {
    setActionLoading(action);
    try {
      let res;
      if (action === "validate") res = await setupApi.validate(code);
      else if (action === "submit") res = await setupApi.submitApproval(code);
      else if (action === "approve") res = await setupApi.approve(code);
      else if (action === "activate") res = await setupApi.activate(code);
      toast.success(res.data.message || `${label} successful`);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.blockers) {
        toast.error(`${label} blocked`, { description: `${detail.blockers.length} blocking condition(s)` });
      } else if (detail?.failures) {
        toast.error(`Validation failed: ${detail.failures.length} issue(s)`);
      } else if (detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : `${label} failed`);
      }
      load();
    } finally {
      setActionLoading(null);
    }
  };

  const lifecycleIdx = LIFECYCLE.indexOf(status);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Back + Header */}
      <div>
        <button onClick={() => navigate("/setup-studio")} className="flex items-center gap-1 text-sm text-[#706E6B] hover:text-[#0176D3] mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Setup Studio
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#181818] font-sans">{domain.name}</h1>
            <p className="text-sm text-[#706E6B] mt-1">{domain.description}</p>
            <p className="text-xs font-mono text-[#706E6B] mt-1">Code: {code}</p>
          </div>
          <StatusBadge status={status} className="text-sm px-3 py-1" />
        </div>
      </div>

      {/* Lifecycle Timeline */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
        <p className="text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-4">Lifecycle Progress</p>
        <div className="flex items-center gap-0">
          {LIFECYCLE.map((s, i) => {
            const done = i <= lifecycleIdx;
            const current = i === lifecycleIdx;
            return (
              <div key={s} className="flex items-center flex-1 min-w-0">
                <div className={`flex flex-col items-center ${i < LIFECYCLE.length - 1 ? "flex-1" : ""}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                    current ? "border-[#0176D3] bg-[#EEF4FF] text-[#0176D3]" :
                    done ? "border-emerald-400 bg-emerald-400/20 text-[#2E844A]" :
                    "border-[#E5E5E5] bg-[#F3F3F3] text-[#706E6B]"
                  }`}>
                    {done && !current ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className={`text-[9px] font-mono mt-1 text-center ${current ? "text-[#0176D3]" : done ? "text-[#2E844A]" : "text-[#706E6B]"}`}>
                    {s.replace("_", " ")}
                  </span>
                </div>
                {i < LIFECYCLE.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 ${i < lifecycleIdx ? "bg-emerald-400/50" : "bg-[#E5E5E5]"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Readiness + Actions Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Readiness Stats */}
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
          <p className="text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-3">Readiness</p>
          <div className="flex items-end gap-2 mb-3">
            <span className="text-3xl font-bold text-[#181818] font-sans">{readiness.percentage || 0}%</span>
            <span className="text-sm text-[#706E6B] mb-1">complete</span>
          </div>
          <div className="h-2 bg-[#F3F3F3] rounded-full mb-3">
            <div className="h-full bg-gradient-to-r from-[#0176D3] to-[#014486] rounded-full transition-all"
              style={{ width: `${readiness.percentage || 0}%` }} />
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { label: "Required", value: readiness.required || 0, color: "text-[#444444]" },
              { label: "Completed", value: readiness.completed || 0, color: "text-[#2E844A]" },
              { label: "Blocked", value: readiness.blocked || 0, color: "text-rose-400" },
            ].map(s => (
              <div key={s.label} className="p-2 rounded-lg bg-[#F3F3F3]">
                <p className={`text-lg font-bold ${s.color} font-sans`}>{s.value}</p>
                <p className="text-[10px] font-mono text-[#706E6B]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Action Panel */}
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
          <p className="text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-3">Actions</p>
          <div className="space-y-2" data-testid="setup-action-panel">
            {canValidate && (
              <ActionBtn
                icon={Play} label="Run Validation" testId={SETUP.validateBtn}
                onClick={() => doAction("validate", "Validation")}
                loading={actionLoading === "validate"}
                disabled={status === "ACTIVE"}
                variant="secondary"
              />
            )}
            {canValidate && status !== "ACTIVE" && (
              <ActionBtn
                icon={Send} label="Submit for Approval" testId={SETUP.submitApprovalBtn}
                onClick={() => doAction("submit", "Submission")}
                loading={actionLoading === "submit"}
                disabled={!["IN_PROGRESS", "CONFIGURED", "VALIDATED"].includes(status)}
                variant="secondary"
              />
            )}
            {canApprove && (
              <ActionBtn
                icon={ThumbsUp} label="Approve Configuration" testId={SETUP.approveBtn}
                onClick={() => doAction("approve", "Approval")}
                loading={actionLoading === "approve"}
                disabled={status !== "VALIDATED"}
                variant="amber"
              />
            )}
            {canActivate && (
              <ActionBtn
                icon={Zap} label="Activate Domain" testId={SETUP.activateBtn}
                onClick={() => doAction("activate", "Activation")}
                loading={actionLoading === "activate"}
                disabled={status === "ACTIVE"}
                variant={status === "APPROVED" && blockers.length === 0 ? "success" : "blocked"}
              />
            )}
          </div>
        </div>
      </div>

      {/* Blockers */}
      {blockers.length > 0 && (
        <div className="bg-rose-500/5 border border-rose-500/30 rounded-xl p-5" data-testid={SETUP.blockersList}>
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-rose-400" />
            <h3 className="text-sm font-semibold text-rose-400">Activation Blockers ({blockers.length})</h3>
          </div>
          <div className="space-y-2">
            {blockers.map((b, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#F3F3F3] border border-rose-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono text-rose-400 uppercase">{b.type}</span>
                  <span className="text-[10px] font-mono text-[#706E6B]">·</span>
                  <span className="text-[10px] font-mono text-rose-400/60">{b.code}</span>
                </div>
                <p className="text-sm text-rose-300">{b.message}</p>
                <p className="text-xs text-[#706E6B] mt-1">Resolution: {b.resolution}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dependencies */}
      {deps.length > 0 && (
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#181818] font-sans mb-3" data-testid={SETUP.dependenciesList}>
            Dependencies ({deps.length})
          </h3>
          <div className="space-y-2">
            {deps.map((d, i) => (
              <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${
                d.is_satisfied ? "bg-emerald-500/5 border-emerald-500/20" : "bg-rose-500/5 border-rose-500/20"
              }`}>
                <div className="flex items-center gap-3">
                  {d.is_satisfied
                    ? <CheckCircle2 className="w-4 h-4 text-[#2E844A]" />
                    : <XCircle className="w-4 h-4 text-rose-400" />
                  }
                  <div>
                    <p className="text-sm text-[#181818]">{d.domain_name}</p>
                    <p className="text-xs text-[#706E6B]">{d.reason}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={d.current_status} />
                  {d.required && <span className="text-[10px] font-mono text-rose-400">Required</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation Results */}
      {valResults.length > 0 && (
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#181818] font-sans mb-3" data-testid={SETUP.validationResults}>
            Validation Rules
          </h3>
          <div className="space-y-2">
            {valResults.map((v, i) => (
              <div key={i} className={`flex items-start justify-between p-3 rounded-lg border ${
                v.result === "PASS" ? "bg-emerald-500/5 border-emerald-500/20" :
                v.result === "FAIL" ? "bg-rose-500/5 border-rose-500/20" :
                v.result === "WARNING" ? "bg-amber-500/5 border-amber-500/20" :
                "bg-[#F3F3F3] border-[#E5E5E5]"
              }`}>
                <div className="flex items-start gap-3">
                  {v.result === "PASS" && <CheckCircle2 className="w-4 h-4 text-[#2E844A] mt-0.5 flex-shrink-0" />}
                  {v.result === "FAIL" && <XCircle className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />}
                  {v.result === "WARNING" && <AlertTriangle className="w-4 h-4 text-[#8C4B00] mt-0.5 flex-shrink-0" />}
                  <div>
                    <p className="text-xs font-mono text-[#706E6B]">{v.rule}</p>
                    <p className="text-sm text-[#444444] mt-0.5">{v.message}</p>
                  </div>
                </div>
                <span className={`text-[10px] font-mono font-bold flex-shrink-0 ml-3 ${
                  v.result === "PASS" ? "text-[#2E844A]" :
                  v.result === "FAIL" ? "text-rose-400" :
                  v.result === "WARNING" ? "text-[#8C4B00]" : "text-[#706E6B]"
                }`}>{v.result}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status History */}
      {history.length > 0 && (
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#181818] font-sans mb-3" data-testid={SETUP.statusHistory}>
            Status History
          </h3>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#F3F3F3] border border-[#EEF4FF]">
                <Clock className="w-3.5 h-3.5 text-[#706E6B] flex-shrink-0" />
                <StatusBadge status={h.to_status} />
                <span className="text-xs text-[#706E6B] flex-1 truncate">{h.reason}</span>
                <span className="text-[10px] font-mono text-[#706E6B]">{h.actor_email}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, loading, disabled, variant = "secondary", testId }) {
  const variants = {
    secondary: "bg-[#EEF4FF] text-[#0176D3] border-[#0176D3]/30 hover:bg-[#E5E5E5]",
    amber: "bg-amber-500/10 text-[#8C4B00] border-amber-500/30 hover:bg-amber-500/20",
    success: "bg-emerald-500/10 text-[#2E844A] border-emerald-500/30 hover:bg-emerald-500/20",
    blocked: "bg-[#F3F3F3] text-[#706E6B] border-[#E5E5E5] cursor-not-allowed",
  };
  return (
    <button
      data-testid={testId}
      onClick={!disabled && !loading ? onClick : undefined}
      disabled={disabled || loading}
      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${variants[disabled ? "blocked" : variant]} ${disabled || loading ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      {label}
      {disabled && !loading && <Lock className="w-3 h-3 ml-auto text-[#706E6B]" />}
      {!disabled && !loading && <ChevronRight className="w-3 h-3 ml-auto" />}
    </button>
  );
}

function LoadingState() {
  return (
    <div className="p-6 space-y-4 animate-pulse">
      <div className="h-8 bg-[#F3F3F3] rounded-lg w-64" />
      <div className="h-20 bg-[#F3F3F3] rounded-xl" />
      <div className="grid grid-cols-2 gap-4">
        <div className="h-40 bg-[#F3F3F3] rounded-xl" />
        <div className="h-40 bg-[#F3F3F3] rounded-xl" />
      </div>
    </div>
  );
}
